import { formatMoney } from "~/utils/formatMoney"
import moment from "moment"
import { CampProps } from "~/interfaces"

type iReservationReport = {
  arrival: Date
  departure: Date
  created_at: Date
  name: string
  site: string
  owed: number
  paid: number
  refunded: number
  refunded_service_fee: number
  taxes: number
  credit_card_fees_collected: number
  service_fee: number
  stripe_fee: number
  net_received: number
  payment_mode: string
  duration: string
  email: string
  phone: string
  notes: string
  site_type: string
  adults: number
  kids: number
  discount_type: string
}

const ReservationsTable = ({
  data,
  camp,
  isCollectingTax,
  isCollectingCreditCardFee,
}: {
  data: iReservationReport[]
  camp: CampProps
  isCollectingTax: boolean
  isCollectingCreditCardFee: boolean
}) => {
  return (
    <table className="min-w-full table-auto divide-y divide-gray-300">
      <thead className="sticky top-0 bg-white shadow-sm">
        <tr>
          <th scope="col" className="pr-4 py-3.5 text-left text-base font-semibold text-gray-900 whitespace-nowrap">
            Arrival
          </th>
          <th scope="col" className="pl-3 py-3.5 text-left text-base font-semibold text-gray-900 whitespace-nowrap">
            Departure
          </th>
          <th scope="col" className="pl-3 py-3.5 text-left text-base font-semibold text-gray-900 whitespace-nowrap">
            Duration
          </th>
          <th scope="col" className="pl-3 py-3.5 text-left text-base font-semibold text-gray-900 whitespace-nowrap">
            Created at
          </th>
          <th
            scope="col"
            className="pl-3 pr-16 py-3.5 text-left text-base font-semibold text-gray-900 whitespace-nowrap"
          >
            Name
          </th>
          <th scope="col" className="pl-3 py-3.5 text-left text-base font-semibold text-gray-900 whitespace-nowrap">
            Email
          </th>
          <th
            scope="col"
            className="pl-3 pr-20 py-3.5 text-left text-base font-semibold text-gray-900 whitespace-nowrap"
          >
            Phone
          </th>
          <th scope="col" className="pl-3 py-3.5 text-right text-base font-semibold text-gray-900 whitespace-nowrap">
            Adults
          </th>
          <th scope="col" className="pl-3 py-3.5 text-right text-base font-semibold text-gray-900 whitespace-nowrap">
            Kids
          </th>
          <th
            scope="col"
            className="pl-3 pr-6 py-3.5 text-left text-base font-semibold text-gray-900 whitespace-nowrap"
          >
            Site
          </th>
          <th
            scope="col"
            className="pl-3 pr-8 py-3.5 text-left text-base font-semibold text-gray-900 whitespace-nowrap"
          >
            Site type
          </th>
          <th scope="col" className="pl-3 py-3.5 text-left text-base font-semibold text-gray-900 whitespace-nowrap">
            Payment Mode
          </th>
          <th scope="col" className="pl-3 py-3.5 text-left text-base font-semibold text-gray-900 whitespace-nowrap">
            Notes
          </th>
          <th scope="col" className="pl-3 py-3.5 text-left text-base font-semibold text-gray-900 whitespace-nowrap">
            Discount
          </th>
          <th scope="col" className="pl-3 py-3.5 text-right text-base font-semibold text-gray-900 whitespace-nowrap">
            Owed
          </th>
          <th scope="col" className="pl-3 py-3.5 text-right text-base font-semibold text-gray-900 whitespace-nowrap">
            Paid
          </th>
          <th scope="col" className="pl-3 py-3.5 text-right text-base font-semibold text-gray-900 whitespace-nowrap">
            Refunded
          </th>
          <th scope="col" className="pl-3 py-3.5 text-right text-base font-semibold text-gray-900 whitespace-nowrap">
            Service Fee
          </th>
          <th scope="col" className="pl-3 py-3.5 text-right text-base font-semibold text-gray-900 whitespace-nowrap">
            Refunded Service Fee
          </th>
          <th scope="col" className="pl-3 py-3.5 text-right text-base font-semibold text-gray-900 whitespace-nowrap">
            Stripe Fee
          </th>
          <th scope="col" className="pl-3 py-3.5 text-right text-base font-semibold text-gray-900 whitespace-nowrap">
            Net Amount
          </th>
          {isCollectingTax && (
            <th scope="col" className="pl-3 py-3.5 text-right text-base font-semibold text-gray-900 whitespace-nowrap">
              Taxes collected
            </th>
          )}
          {isCollectingCreditCardFee && (
            <th scope="col" className="pl-3 py-3.5 text-right text-base font-semibold text-gray-900 whitespace-nowrap">
              Credit Card Fees Collected
            </th>
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 bg-white">
        {data &&
          data.map((reservationData: any, index: number) => {
            const {
              arrival,
              departure,
              created_at,
              owed,
              paid,
              refunded,
              refunded_service_fee,
              taxes,
              net_received,
              credit_card_fees_collected,
              service_fee,
              stripe_fee,
              payment_mode,
              notes,
              duration,
              site,
              name,
              email,
              phone,
              site_type,
              adults,
              kids,
              discount_type,
            } = reservationData

            return (
              <tr className="h-10 hover:bg-gray-50" key={index}>
                <td className="text-left font-bold">{moment(arrival).format("MM-DD-YY")}</td>
                <td className="pl-3 text-left text-gray-500">{moment(departure).format("MM-DD-YY")}</td>
                <td className="pl-3 text-left text-gray-500">{duration}</td>
                <td className="pl-3 text-left text-gray-500">{moment(created_at).format("MM-DD-YY")}</td>
                <td className="pl-3 text-left text-gray-500">{name}</td>
                <td className="pl-3 text-left text-gray-500">{email}</td>
                <td className="pl-3 text-left text-gray-500">{phone}</td>
                <td className="pl-3 text-right text-gray-500">{adults > 0 ? adults : "-"}</td>
                <td className="pl-3 text-right text-gray-500">{kids > 0 ? kids : "-"}</td>
                <td className="pl-3 text-left text-gray-500">{site}</td>
                <td className="pl-3 text-left text-gray-500">{site_type}</td>
                <td className="pl-3 text-left text-gray-500">{payment_mode}</td>
                <td className="pl-3 text-left text-gray-500 max-w-72 truncate">{notes}</td>
                <td className="pl-3 text-left text-gray-500">{discount_type}</td>
                <td className="pl-3 text-right text-gray-500">{owed > 0 ? formatMoney(owed, camp?.currency) : "-"}</td>
                <td className="pl-3 text-right text-gray-500">{paid > 0 ? formatMoney(paid, camp?.currency) : "-"}</td>
                <td className="pl-3 text-right text-gray-500">
                  {refunded > 0 ? formatMoney(refunded, camp?.currency) : "-"}
                </td>
                <td className="pl-3 text-right text-gray-500">
                  {service_fee > 0 ? formatMoney(service_fee, camp?.currency) : "-"}
                </td>
                <td className="pl-3 text-right text-gray-500">
                  {refunded_service_fee > 0 ? formatMoney(refunded_service_fee, camp?.currency) : "-"}
                </td>
                <td className="pl-3 text-right text-gray-500">
                  {stripe_fee > 0 ? formatMoney(stripe_fee, camp?.currency) : "-"}
                </td>
                <td className="pl-3 text-right text-gray-500">
                  {net_received != null ? formatMoney(net_received, camp?.currency) : "-"}
                </td>
                {isCollectingTax && (
                  <td className="pl-3 text-right text-gray-500">
                    {taxes > 0 ? formatMoney(taxes, camp?.currency) : "-"}
                  </td>
                )}
                {isCollectingCreditCardFee && (
                  <td className="pl-3 text-right text-gray-500">
                    {credit_card_fees_collected > 0 ? formatMoney(credit_card_fees_collected, camp?.currency) : "-"}
                  </td>
                )}
              </tr>
            )
          })}
      </tbody>
    </table>
  )
}

export default ReservationsTable
